import React, { useState, useEffect, useRef } from "react";
import { getCookie } from "../../utils/cookieManager";
import { cookieNames } from "../../constants/constants";
import MessageDialog from "../../../components/dialogBox/messageDialog";
import { useNavigate } from "react-router-dom";
import { resetAllStoreStates } from "../../../store/store";
import useLogout from "../../hooks/useLogout";
import useSessionManager from "../../hooks/useSessionManager";
import { SESSION_CONFIG } from "../../config/sessionConfig";

const TIMER_THRESHOLD = 2 * 60 * 1000; // 2 minutes in milliseconds

const SessionExpirationTimer = () => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  // Derived from SESSION_CONFIG rather than hardcoded. These literals used to
  // shadow the config for the only component that mounts the session manager,
  // so `activityExtension: 15` was the real idle timeout and editing
  // SESSION_CONFIG had no effect at all. useSessionManager takes minutes.
  const { getRemainingTime, isSessionExpiringSoon, checkSessionStatus } = useSessionManager({
    warningThreshold: SESSION_CONFIG.WARNING_THRESHOLD / (60 * 1000),
    activityExtension: SESSION_CONFIG.ACTIVITY_EXTENSION / (60 * 1000),
    heartbeatInterval: SESSION_CONFIG.HEARTBEAT_INTERVAL / (60 * 1000),
  });

  const [showPopup, setShowPopup] = useState(false);
  const [expirationTime, setExpirationTime] = useState(1000);
  
  // Use refs to avoid dependency issues
  const getRemainingTimeRef = useRef(getRemainingTime);
  const isSessionExpiringSoonRef = useRef(isSessionExpiringSoon);
  const logoutRef = useRef(logout);
  
  // Update refs when functions change
  getRemainingTimeRef.current = getRemainingTime;
  isSessionExpiringSoonRef.current = isSessionExpiringSoon;
  logoutRef.current = logout;

  const handleCloseMessageModal = () => {
    setShowPopup(false);
    if (expirationTime <= 0) {
      resetAllStoreStates();
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    const checkTokenExpiration = async () => {
      const jwtToken = getCookie(cookieNames.jwtToken);
      
      // If there's no JWT token, user should be logged out
      if (!jwtToken) {
        const currentPageUrl = window.location.pathname;
        if (
          !currentPageUrl.includes("/signin") &&
          !currentPageUrl.includes("/signup") &&
          !currentPageUrl.includes("/forgotPassword") &&
          !currentPageUrl.includes("/sendOtp") &&
          !currentPageUrl.includes("/verifyOtp") &&
          !currentPageUrl.includes("/auth/callback") &&
          currentPageUrl !== "/"
        ) {
          console.log("Calling logout due to missing JWT token");
          logout();
        }
        return;
      }
      
      // Get remaining time using session manager
      const timeRemaining = getRemainingTimeRef.current();
      setExpirationTime(timeRemaining);
      
      // Check if session is expiring soon
      if (isSessionExpiringSoonRef.current()) {
        const currentPageUrl = window.location.href;
        if (
          currentPageUrl.indexOf("/signin") <= 0 &&
          currentPageUrl.indexOf("/signup") <= 0
        ) {
          setShowPopup(true);
        }
      }
      
      // If session is expired, logout
      if (timeRemaining <= 0) {
        console.log("Session expired, logging out");
        logoutRef.current();
      }
    };
    
    checkTokenExpiration();

    // Check every 30 seconds for more responsive handling
    const intervalId = setInterval(checkTokenExpiration, 30 * 1000);

    return () => clearInterval(intervalId);
  }, []); // Remove dependencies to prevent infinite re-renders

  return (
    <div>
      {showPopup && (
        <MessageDialog
          message={"Your session is about to expire!"}
          open={showPopup}
          onClose={handleCloseMessageModal}
        />
      )}
    </div>
  );
};

export default SessionExpirationTimer;